import React from 'react';
import { DatePicker, Tag, TimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useFilterLayoutStyles } from 'utils/languageUtils';

export const PAGE_SIZE_OPTIONS = ['20', '50', '100', '200', '500'];

export const DEFAULT_PAGINATION = { current: 1, pageSize: 20 };

/** ID 输入解析：空 → undefined；非纯数字 → null（调用方提示） */
export const parseIdInput = (value?: string): number | null | undefined => {
    const trimmed = value?.trim();
    if (!trimmed) return undefined;
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

/** tms_task.task_status，文案对齐 Java 枚举 TmsTaskStatus 的中文注释 */
export const TASK_STATUS_MAP: Record<number, { label: string; color: string }> = {
    1: { label: '草稿', color: 'default' },
    2: { label: '已发布', color: 'blue' },
    3: { label: '进行中', color: 'processing' },
    4: { label: '已完成', color: 'green' },
    5: { label: '异常', color: 'red' },
    6: { label: '已取消', color: 'default' },
};

export const TASK_STATUS_OPTIONS = Object.entries(TASK_STATUS_MAP).map(([value, item]) => ({
    value: Number(value),
    label: item.label,
}));

type TagMap = Record<number, { label: string; color: string }>;

/**
 * 以下四组枚举在已上线 TMS 任务列表 / 路线页均为纯文本、无既有配色，
 * 此处按 antd 预设色自定，同一列内保证区分度；「系统创建」沿用站内「来源：系统」标签的 blue。
 */
export const TASK_TYPE_MAP: TagMap = {
    1: { label: '常规', color: 'blue' },
    2: { label: '临时', color: 'orange' },
    3: { label: '三方', color: 'purple' },
};
export const TASK_SOURCE_MAP: TagMap = {
    1: { label: '系统创建', color: 'blue' },
    2: { label: '人工创建', color: 'gold' },
};
export const BUSINESS_TYPE_MAP: TagMap = {
    1: { label: '自有路线', color: 'green' },
    2: { label: 'TikTok', color: 'blue' },
    3: { label: 'Temu_GA', color: 'orange' },
    4: { label: 'Temu_SCF', color: 'magenta' },
};
/** 对齐 TmsRouteTypeEnum：1-LINEHAUL 2-FEEDER 3-HANDOVER */
export const ROUTE_TYPE_MAP: TagMap = {
    1: { label: '干线', color: 'blue' },
    2: { label: '支线', color: 'cyan' },
    3: { label: '交接', color: 'purple' },
};

/** 是否删除筛选：0-未删除 1-已删除，不选=全部（默认） */
export const IS_DELETED_OPTIONS = [
    { value: 0, label: '未删除' },
    { value: 1, label: '已删除' },
];

/** 是否删除列：与筛选项文案一致 */
export const renderDeleted = (value: boolean): React.ReactNode =>
    value ? <Tag color="red">已删除</Tag> : <Tag>未删除</Tag>;

export const toOptions = (map: TagMap) =>
    Object.entries(map).map(([value, item]) => ({ value: Number(value), label: item.label }));

/** 带色 Tag 渲染，样式同任务状态列 */
export const renderTagged = (map: TagMap) => (value: number | null): React.ReactNode => {
    if (value === null || value === undefined) return '-';
    const item = map[value];
    return item ? <Tag color={item.color}>{item.label}</Tag> : <Tag>{value}</Tag>;
};

/** tms_task_waypoint.waypoint_status，文案对齐表注释：1-待到站 2-已到站 4-待发车 3-已发车 */
export const WAYPOINT_STATUS_MAP: Record<number, { label: string; color: string }> = {
    1: { label: '待到站', color: 'default' },
    2: { label: '已到站', color: 'blue' },
    4: { label: '待发车', color: 'orange' },
    3: { label: '已发车', color: 'green' },
};

/** 途经点状态的业务流转顺序（待到站 → 已到站 → 待发车 → 已发车）。JS 对象整数键按数值迭代，上面 map 写成 1,2,4,3 也会被排成 1,2,3,4，下拉需按此数组取序 */
export const WAYPOINT_STATUS_ORDER: number[] = [1, 2, 4, 3];

export const WAYPOINT_TYPE_MAP: Record<number, string> = { 1: '起点', 2: '途经点', 3: '终点' };

export const renderTaskStatus = (value: number | null): React.ReactNode => {
    if (value === null || value === undefined) return '-';
    const item = TASK_STATUS_MAP[value];
    return item ? <Tag color={item.color}>{item.label}</Tag> : <Tag>{value}</Tag>;
};

export const renderWaypointStatus = (value: number | null): React.ReactNode => {
    if (value === null || value === undefined) return '-';
    const item = WAYPOINT_STATUS_MAP[value];
    return item ? <Tag color={item.color}>{item.label}</Tag> : <Tag>{value}</Tag>;
};

export const renderMapped = (map: Record<number, string>) => (value: number | null): string =>
    value === null || value === undefined ? '-' : map[value] ?? String(value);

export const renderText = (value: string | number | null): string | number =>
    value === null || value === undefined || value === '' ? '-' : value;

/** 时间列：不换行，避免 'yyyy-MM-dd HH:mm:ss' 在列宽临界时折成两行 */
export const renderTime = (value: string | null): React.ReactNode =>
    value ? <span className="whitespace-nowrap">{value}</span> : '-';

/** 本地时间 + 时区缩写（ET/CT/MT/PT）拼接，照 tms 任务列表的展示方式；本地时间为空则整体显示 '-' */
export const renderLocalWithTimezone = <T extends Record<string, unknown>>(timezoneKey: keyof T) =>
    (value: string | null, record: T): React.ReactNode => {
        if (!value) return '-';
        const tz = record[timezoneKey];
        return <span className="whitespace-nowrap">{[value, tz].filter(Boolean).join(' ')}</span>;
    };

/** UTC 时间点输入格式：yyyy-MM-dd HH:mm:ss（与后端 LocalDateTime 标量一致） */
export const UTC_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

/** 只接受 IANA 时区名（含「/」，如 America/Los_Angeles）；仓库回退列可能是「GMT-4 / UTC-7」这类写法，dayjs 无法识别，视为时区缺失 */
export const usableZone = (zone: string | null | undefined): string | null => (zone && zone.includes('/') ? zone : null);

/** 解锁快照与当前表单值不同的字段数：两抽屉「上锁即放弃」的确认文案用（用户 2026-10-21） */
export const countChangedFields = (snapshot: Record<string, string>, current: Record<string, string>): number =>
    Array.from(new Set([...Object.keys(snapshot), ...Object.keys(current)])).filter((key) => (snapshot[key] ?? '') !== (current[key] ?? '')).length;

/** 解锁态提示末尾统一追加（用户 2026-10-21：上锁 = 放弃解锁期间未保存的修改） */
export const RELOCK_DISCARD_HINT = '重新上锁会放弃解锁期间未保存的修改。';

// 字符串 ↔ Dayjs 按固定格式严格互转需要 customParseFormat 插件
dayjs.extend(customParseFormat);

export type OpsDateTimeKind = 'date' | 'time' | 'datetime';
const OPS_DATE_TIME_FORMATS: Record<OpsDateTimeKind, string> = { date: 'YYYY-MM-DD', time: 'HH:mm:ss', datetime: 'YYYY-MM-DD HH:mm:ss' };

type OpsDateTimeItemProps = {
    label: string;
    kind: OpsDateTimeKind;
    /** 表单值仍按字符串持有（与后端标量格式一致），空串 = 未填 */
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    required?: boolean;
    spanClassName?: string;
};

/**
 * 运维抽屉的日期 / 时间 / 日期时间选择项：布局与 InputFilterItem / DatePickerFilter 一致（标签 + 控件水平排布），
 * 对外值为后端标量格式字符串（yyyy-MM-dd / HH:mm:ss / yyyy-MM-dd HH:mm:ss），避免手敲格式出错。
 */
export const OpsDateTimeItem: React.FC<OpsDateTimeItemProps> = ({ label, kind, value, onChange, disabled, required, spanClassName = 'w-[9em]' }) => {
    const { containerClassName, labelClassName, inputContainerClassName } = useFilterLayoutStyles(spanClassName, undefined, true);
    const format = OPS_DATE_TIME_FORMATS[kind];
    const parsed: Dayjs | null = value ? dayjs(value, format, true) : null;
    const picked = parsed && parsed.isValid() ? parsed : null;
    const handle = (d: Dayjs | null) => onChange(d ? d.format(format) : '');
    return (
        <div className={containerClassName}>
            <span className={labelClassName}>
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </span>
            <div className={inputContainerClassName}>
                {kind === 'time' ? (
                    <TimePicker value={picked} onChange={handle} format={format} className="w-full" disabled={disabled} placeholder={disabled ? '' : format} />
                ) : (
                    <DatePicker
                        value={picked}
                        onChange={handle}
                        format={format}
                        showTime={kind === 'datetime' ? { format: 'HH:mm:ss' } : false}
                        className="w-full"
                        disabled={disabled}
                        placeholder={disabled ? '' : format}
                    />
                )}
            </div>
        </div>
    );
};
